import type { AxiosInstance } from 'axios';

/**
 * Courier money — what Shiprocket bookings cost, and the evidence for it.
 *
 * Estimates are recorded when a cost is incurred (booking, RTO, reverse
 * pickup); actual amounts come from the Shiprocket wallet passbook an admin
 * imports. Amounts are integer paise.
 */

export type ShipmentChargeType =
  | 'FORWARD_FREIGHT'
  | 'COD_FEE'
  | 'RTO_FREIGHT'
  | 'REVERSE_FREIGHT'
  | 'WEIGHT_DISCREPANCY'
  | 'CANCELLATION_CREDIT'
  | 'INSURANCE'
  | 'OTHER_ADJUSTMENT';

export interface ShipmentCharge {
  id: string;
  subOrderId: string;
  shipmentId: string | null;
  returnShipmentId: string | null;
  awbNumber: string | null;
  type: ShipmentChargeType;
  source: 'QUOTE' | 'PROVIDER_STATEMENT' | 'MANUAL_ADJUSTMENT';
  status: 'ESTIMATED' | 'RECONCILED' | 'PENDING_EVIDENCE' | 'VOID';
  bearer: 'SELLER' | 'PLATFORM';
  estimatedPaise: number | null;
  actualPaise: number | null;
  recoveredPaise: number;
  providerRef: string | null;
  occurredAt: string;
  notes: string | null;
}

export interface WalletTxn {
  id: string;
  providerRef: string;
  occurredAt: string;
  amountPaise: number;
  balanceAfterPaise: number | null;
  awbNumber: string | null;
  description: string | null;
  category: string;
  matchStatus: 'UNMATCHED' | 'MATCHED' | 'IGNORED';
}

export interface CourierExceptions {
  counts: {
    chargesAwaitingEvidence: number;
    awbWithoutCharge: number;
    unmatchedWalletLines: number;
    ambiguousBookings: number;
  };
  /** The last balance a passbook stated — never the live wallet balance. */
  walletBalance: { balancePaise: number; asOf: string; source: 'PASSBOOK_IMPORT' } | null;
  pendingEvidence: ShipmentCharge[];
  staleEstimates: ShipmentCharge[];
  awbWithoutCharge: Array<{
    id: string;
    subOrderId: string;
    awbNumber: string | null;
    status: string;
    freightChargePaise: number;
  }>;
  cancelledAwaitingCredit: ShipmentCharge[];
  ambiguousBookings: Array<{
    id: string;
    subOrderId: string;
    bookingError: string | null;
    providerShipmentId: string | null;
    updatedAt: string;
  }>;
  ambiguousReturnBookings: Array<{
    id: string;
    returnRequestId: string;
    bookingError: string | null;
    providerShipmentId: string | null;
    updatedAt: string;
  }>;
  failedCancellations: Array<{
    id: string;
    subOrderId: string;
    awbNumber: string | null;
    cancellationError: string | null;
  }>;
  unmatchedWallet: WalletTxn[];
  unrecoveredOnClosed: ShipmentCharge[];
  shippingShortfalls: Array<{ subOrderId: string; customerPaise: number; actualPaise: number }>;
}

export interface PassbookRow {
  ref?: string;
  occurredAt: string;
  /** Signed from the wallet's side: negative = debit. */
  amountPaise: number;
  balanceAfterPaise?: number;
  awbNumber?: string;
  description?: string;
}

export interface PassbookImportResult {
  importId: string;
  imported: number;
  duplicates: number;
  matched: number;
  unmatched: number;
  recharges: number;
  balanceBreaks: Array<{ providerRef: string; expectedPaise: number; statedPaise: number }>;
}

export interface OutboxRow {
  id: string;
  kind: string;
  status: 'PENDING' | 'PROCESSING' | 'DONE' | 'DEAD';
  attempts: number;
  lastError: string | null;
  createdAt: string;
  nextAttemptAt: string;
}

export type ResolveBookingInput =
  | { outcome: 'CREATED'; providerOrderId: string; providerShipmentId: string }
  | { outcome: 'NOT_CREATED' }
  | { outcome: 'BOOKED'; awbNumber: string; courierName?: string }
  | { outcome: 'NOT_BOOKED' };

export interface Paged<T> {
  data: T[];
  page: { page: number; pageSize: number; total: number; pageCount: number };
}

export function courierFinanceEndpoints(http: AxiosInstance) {
  return {
    exceptions: (limit?: number) =>
      http
        .get<CourierExceptions>('/admin/shipping/reconciliation', {
          params: limit ? { limit } : undefined,
        })
        .then((r) => r.data),
    legacyReport: () =>
      http
        .get<Record<string, unknown>>('/admin/shipping/reconciliation/legacy')
        .then((r) => r.data),
    packagePosition: (subOrderId: string) =>
      http
        .get<Record<string, unknown>>(`/admin/shipping/packages/${subOrderId}/position`)
        .then((r) => r.data),
    importPassbook: (body: {
      fileName?: string;
      openingBalancePaise?: number;
      closingBalancePaise?: number;
      rows: PassbookRow[];
    }) =>
      http.post<PassbookImportResult>('/admin/shipping/wallet/imports', body).then((r) => r.data),
    walletTransactions: (params: {
      page?: number;
      pageSize?: number;
      status?: 'UNMATCHED' | 'MATCHED' | 'IGNORED';
    }) =>
      http
        .get<Paged<WalletTxn>>('/admin/shipping/wallet/transactions', { params })
        .then((r) => r.data),
    rematch: () =>
      http
        .post<{
          matched: number;
          unmatched: number;
          recharges: number;
        }>('/admin/shipping/wallet/rematch')
        .then((r) => r.data),
    ignoreWalletTxn: (txnId: string, reason: string) =>
      http
        .post<WalletTxn>(`/admin/shipping/wallet/transactions/${txnId}/ignore`, { reason })
        .then((r) => r.data),
    voidCharge: (chargeId: string, reason: string) =>
      http
        .post<ShipmentCharge>(`/admin/shipping/charges/${chargeId}/void`, { reason })
        .then((r) => r.data),
    resolveBooking: (shipmentId: string, body: ResolveBookingInput) =>
      http.post<unknown>(`/admin/shipping/${shipmentId}/resolve-booking`, body).then((r) => r.data),
    resolveReturnBooking: (returnShipmentId: string, body: ResolveBookingInput) =>
      http
        .post<unknown>(`/admin/shipping/returns/${returnShipmentId}/resolve-booking`, body)
        .then((r) => r.data),
    outbox: (params: { status?: OutboxRow['status']; page?: number; pageSize?: number }) =>
      http.get<Paged<OutboxRow>>('/admin/finance/outbox', { params }).then((r) => r.data),
    retryOutbox: (id: string) =>
      http
        .post<{ id: string; status: string | null }>(`/admin/finance/outbox/${id}/retry`)
        .then((r) => r.data),
  };
}

export type CourierFinanceEndpoints = ReturnType<typeof courierFinanceEndpoints>;
